import {
  SiteModel,
  AssetModel,
  ZoneNameModel,
  ZoneCoordinate,
  SiteFencingModel,
  ZoneFencingModel,
} from "src/app/store/models/asset.model";
import {
  DRAFT_COLOR,
  DROPPED_COLOR,
  COMPLETED_COLOR,
  createCustomMarker,
  applyDraftMarkerStyles,
  applyDroppedMarkerStyles,
  applyPseudoElementStyles,
  applyCompletedMarkerStyles,
  applyRejectApproveMarkerStyle,
} from "src/app/utils/asset-map/asset.marker.util";
import { BehaviorSubject } from "rxjs";
import { Capacitor } from "@capacitor/core";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "src/environments/environment";
import { UserModel } from "src/app/store/models/user.model";
import { Library, Loader } from "@googlemaps/js-api-loader";
import { MAPBACKGROUND } from "src/app/utils/constant.util";
import { QRModel } from "src/app/store/models/site-qr.model";
import { LoadingController } from "@ionic/angular/standalone";
import { ElementRef, inject, Injectable } from "@angular/core";
import { HttpService } from "../../http-service/http-client.service";
import { LocationService } from "../../location-service/location.service";
import { StructuralMapService } from "../structural-map/structural-map.service";
import { MM_LinkedAssetModel } from "src/app/store/models/materialManagement.model";
import { createCustomQRMarker } from "src/app/utils/site-setup-map/site-setup-map.utils";
@Injectable({ providedIn: "root" })
export class SettingsMapService {
  httpService = inject(HttpService);

  location = inject(LocationService);
  loadingCtrl = inject(LoadingController);
  private translate = inject(TranslateService);
  public structuralMap = inject(StructuralMapService);

  // ** Map Variables
  public site: SiteModel;
  public siteUniqueId: string;
  public map: google.maps.Map;
  public zoneNames: ZoneNameModel[] = [];
  private mapOptions: google.maps.MapOptions;
  public siteCoordinates: SiteFencingModel[];
  public zoneCoordinates: ZoneFencingModel[];
  private sitePolygons: google.maps.Polygon[] = [];
  private zonePolygons: google.maps.Polygon[] = [];
  private isFencingVisible = true;
  public qrCount = new BehaviorSubject<QRModel[]>([]);
  public assets = new BehaviorSubject<AssetModel[]>([]);
  public majorAssetsCount = new BehaviorSubject<number>(0);
  public zoneCoveragePercentage: number = 0;
  public markers: google.maps.marker.AdvancedMarkerElement[] = [];
  public isFieldSetupSummary = new BehaviorSubject<boolean>(false);
  public qr_markers: google.maps.marker.AdvancedMarkerElement[] = [];
  public isStructuralAssetSummary = new BehaviorSubject<boolean>(false);
  public coverageValidationFailed = new BehaviorSubject<boolean>(false);
  public isEditorsModalOpenSubject = new BehaviorSubject<boolean>(false);
  // ** Map Variables

  public isSecurityModalOpen = new BehaviorSubject<boolean>(false);

  // ** Returns correct API key based on platform
  private mapKey(): string {
    return Capacitor.isNativePlatform()
      ? environment.nativeMapKey
      : environment.browserMapKey;
  }

  // ** Google Maps Loader ** //
  private readonly loader: Loader = new Loader({
    region: "US",
    language: "en",
    version: "3.64",
    apiKey: this.mapKey(),
  });
  // ** Google Maps Loader ** //

  constructor() {}

  // ** Initialize Map Options
  private async initializeMapOptions(): Promise<google.maps.MapOptions> {
    return {
      tilt: 0,
      zoom: 2,
      minZoom: 2,
      maxZoom: 21,
      mapId: "g-maps",
      draggable: false,
      mapTypeControl: false,
      disableDefaultUI: true,
      mapTypeId: "styled_map",
      gestureHandling: "none",
      streetViewControl: false,
      keyboardShortcuts: false,
      center: { lat: 20, lng: 0 },
      disableDoubleClickZoom: true,
    };
  }
  // ** Initialize Map Options

  // ** Initialize Map
  public async InitializeSettingsMap(mapRef: ElementRef, user?: UserModel) {
    // let location = this.location.currentLocation;
    const { Map } = await this.importMapsLibrary("maps");
    const options = await this.initializeMapOptions();
    this.map = new Map(mapRef.nativeElement, options);
    this.AddMapStyles(user);
    // this.SetMapCenter(null, location);
  }
  // ** Initialize Map

  // ** Set Map Center
  public SetMapCenter(
    site?: SiteModel | null,
    location?: google.maps.LatLngLiteral | null,
  ) {
    let lat: number, lng: number;

    if (site?.locations?.site?.coordinate) {
      lng = site?.locations?.site?.coordinate?.lng!;
      lat = site?.locations?.site?.coordinate?.lat!;
    } else {
      lng = location?.lng ? location?.lng : 0;
      lat = location?.lat ? location?.lat : 20;
    }

    const position = new google.maps.LatLng(lat, lng);

    // Smooth, non-blocking pan
    requestAnimationFrame(() => {
      this.map.setZoom(14);
      this.map.panTo(position);
    });
  }
  // ** Set Map Center

  // ** Store Subscription
  public SubscribeToSiteStore(site: SiteModel) {
    if (site && site?.id) {
      this.site = site;
      this.RemovePolygons();

      this.majorAssetsCount.next(
        this.site?.assets?.filter((a) => a.isMajor).length ?? 0,
      );

      this.siteCoordinates =
        site.locations?.site?.coordinate?.site_coordinates!;
      this.zoneCoordinates =
        site.locations?.site?.coordinate?.zone_coordinates!;

      this.zoneNames = site.locations?.site?.coordinate?.zone_names ?? [];

      this.siteUniqueId = this.site.locations?.site?.unique_id!;

      // this.map.setZoom(14);
      this.HandlePolygons();
      this.HandleSiteCoverage(site);
      this.SetMapCenter(this.site);
      this.HandleAssetMarkers(site);
    }
    // this.GetAllQr();
  }
  // ** Store Subscription

  // ** Polygon Fencing
  private async HandlePolygons(): Promise<void> {
    const { Polygon } = await this.importMapsLibrary("maps");

    this.addSitePolygons(Polygon);
    this.addZonePolygons(Polygon);
  }

  private addSitePolygons(Polygon: typeof google.maps.Polygon): void {
    this.siteCoordinates?.forEach((siteCoords: SiteFencingModel) => {
      const coordinates = (siteCoords?.coordinates ?? []).map((coord) => ({
        lat: coord.lat,
        lng: coord.lng,
      }));

      if (coordinates && coordinates?.length) {
        const polygon: google.maps.Polygon = new Polygon({
          zIndex: 1,
          visible: this.isFencingVisible,
          map: this.map,
          editable: false,
          strokeWeight: 2,
          fillOpacity: 0.3,
          strokeOpacity: 1,
          paths: coordinates,
          fillColor: "#05B9EF1A",
          strokeColor: "#05B9EF",
        });
        this.sitePolygons.push(polygon);
      }
    });
  }

  private async addZonePolygons(Polygon: typeof google.maps.Polygon) {
    this.zoneCoordinates?.forEach((zoneCoords: ZoneFencingModel) => {
      const coordinates = (zoneCoords?.coordinates ?? []).map((coord) => ({
        lat: coord.lat,
        lng: coord.lng,
        zoneName: coord?.zoneName?.trim(),
      }));

      if (coordinates && coordinates?.length) {
        const matchedZone = this.zoneNames?.find(
          (zone) => zone.id?.toLowerCase() === zoneCoords.id?.toLowerCase(),
        );
        if (!matchedZone) {
          return;
        }

        const polygon: google.maps.Polygon = new Polygon({
          zIndex: 1,
          map: this.map,
          visible: this.isFencingVisible,
          editable: false,
          strokeWeight: 2,
          fillOpacity: 0.3,
          strokeOpacity: 1,
          paths: coordinates,
          fillColor: "#76FF0333",
          strokeColor: "#76FF03",
        });
        this.zonePolygons.push(polygon);
      }
    });
  }
  // ** Polygon Fencing

  // ** Add Markers
  private HandleAssetMarkers(site: SiteModel) {
    if (site.assets && site.assets?.length) {
      this.assets.next(site.assets ?? []);
      this.assets
        ?.getValue()
        ?.filter((asset) => !asset.pid)
        ?.forEach((asset) => this.ProcessAssetMarkers(asset));
    }
  }

  //** Get all QR  */
  public async GetAllQr() {
    const loading = await this.loadingCtrl.create({
      spinner: "circles",
      message: this.translate.instant("APP_LOADING"),
    });
    await loading.present();
    this.httpService
      .GetQRFromSiteByIdBogPro({ uniqueId: this.siteUniqueId })
      .subscribe({
        next: (qr_data) => {
          qr_data.data?.forEach((qr: QRModel) => {
            if (qr.latLong) {
              const [lat, lng] = qr.latLong.split(",").map(parseFloat);
              const position: google.maps.LatLngLiteral = { lat, lng };
              this.AddQrMarker(position, qr);
              this.qrCount.next(qr_data.data);
            } else {
              return;
            }
          });
        },
        error: (err) => {
          console.error("Error fetching QR data:", err);
          loading.dismiss();
        },
        complete: () => {
          loading.dismiss();
        },
      });
  }

  //** Adding QR marker */
  public async AddQrMarker(
    position: google.maps.LatLngLiteral,
    qr: QRModel | undefined,
  ) {
    const { markerContainer } = await createCustomQRMarker(qr!, false);
    const { AdvancedMarkerElement } = await this.importMarkersLibrary("marker");

    const marker = new AdvancedMarkerElement({
      map: this.map,
      position: position,
      gmpClickable: true,
      gmpDraggable: true,
      content: markerContainer,
    });
    this.qr_markers.push(marker);
  }

  private ProcessAssetMarkers(asset: AssetModel) {
    const subAssets = this.assets
      .getValue()
      .filter((subAsset) => subAsset.pid === asset.uid);

    const isSubAssetsValid = subAssets.every(
      (subAsset) =>
        subAsset?.id &&
        subAsset?.assetInfo &&
        subAsset?.assetInfo?.assetStatus &&
        subAsset.assetInfo.assetCostCenter &&
        subAsset.assetInfo.yearOfConstruction,
    );

    const isCompleted =
      isSubAssetsValid &&
      asset?.assetInfo?.assetCostCenter &&
      asset?.assetInfo?.assetStatus &&
      asset.assetInfo.yearOfConstruction
        ? true
        : false;

    const { assetInfo } = asset;
    const { iconPath, icon_url = "asset-icons/placeholder/icon.png" } =
      assetInfo || {};
    const { coordinates } = assetInfo?.assetZone || {};

    if (assetInfo && (iconPath || icon_url) && coordinates) {
      const id = asset.uid!;
      const icon = iconPath || icon_url;
      this.AddOrUpdateMarker(id, icon, asset, isCompleted, coordinates);
    }
  }

  private AddOrUpdateMarker(
    id: string,
    icon: string,
    asset: AssetModel,
    isSubAssetsValid: boolean,
    coordinates: google.maps.LatLngLiteral,
  ) {
    const marker = this.markers?.find((marker) => {
      const markerId = (marker.content as HTMLElement)
        .querySelector("div[id]")
        ?.getAttribute("id");
      return markerId === id;
    });

    const content = marker?.content as HTMLElement;
    if (marker) {
      this.UpdateMarker(asset, content, isSubAssetsValid);
    } else {
      this.AddMarker(id, icon, asset, isSubAssetsValid, coordinates);
    }
  }

  public async AddMarker(
    id: string,
    icon: string,
    asset: AssetModel,
    isCompleted: boolean,
    position: google.maps.LatLngLiteral,
  ) {
    const subAssetsCount = this.structuralMap.GetSubAssets(asset.uid!).length;
    const { AdvancedMarkerElement } = await this.importMarkersLibrary("marker");
    const { markerContainer } = await createCustomMarker(
      id,
      icon,
      asset,
      isCompleted,
      subAssetsCount,
    );

    const marker = new AdvancedMarkerElement({
      map: this.map,
      position: position,
      gmpClickable: true,
      gmpDraggable: false,
      content: markerContainer,
    });
    this.markers.push(marker);
  }

  public UpdateMarker(
    asset: AssetModel,
    marker: HTMLElement,
    isCompleted: boolean,
  ) {
    const mapMarker = marker.querySelector(".mapMarker") as HTMLElement;
    const status = mapMarker.querySelector(".markerStatus") as HTMLElement;
    const subAssetsCount = this.structuralMap.GetSubAssets(asset.uid!).length;
    const markerAfter = mapMarker.querySelector(".markerAfter") as HTMLElement;

    if (!asset.id) {
      applyDroppedMarkerStyles(mapMarker);
      applyPseudoElementStyles(DROPPED_COLOR, markerAfter);
      return;
    }

    if (status && status.parentNode === mapMarker) {
      mapMarker.removeChild(status);
    }

    const isRejected =
      asset.assetStatus?.status.assetRejected ||
      asset.assetStatus?.status.assetDecommissionRejected ||
      asset.assetStatus?.status.assetDemolitionRejected
        ? true
        : false;
    const isApproved =
      !asset.assetStatus?.status.assetDecommissionedPending &&
      !asset.assetStatus?.status.assetDemolitionPending &&
      asset.assetStatus?.status.assetRegistered
        ? true
        : false;
    const isDecomissionDemolish =
      (asset.assetStatus?.status.assetRegistered &&
        asset.assetStatus?.status.assetDecommissionedPending) ||
      (asset.assetStatus?.status.assetRegistered &&
        asset.assetStatus?.status.assetDemolitionPending)
        ? true
        : false;

    if (isCompleted) {
      applyRejectApproveMarkerStyle(
        mapMarker,
        isRejected,
        isApproved,
        isDecomissionDemolish,
        false,
        subAssetsCount,
      );
      // const newStatus = mapMarker.querySelector(".markerStatus") as HTMLElement;
      // if (newStatus && newStatus.parentNode === mapMarker) {
      //   mapMarker.removeChild(newStatus);
      // }
      applyCompletedMarkerStyles(mapMarker);
      applyPseudoElementStyles(COMPLETED_COLOR, markerAfter);
    } else {
      const newStatus = mapMarker.querySelector(".markerStatus") as HTMLElement;
      if (newStatus && newStatus.parentNode === mapMarker) {
        mapMarker.removeChild(newStatus);
      }
      applyDraftMarkerStyles(mapMarker, subAssetsCount);
      applyPseudoElementStyles(DRAFT_COLOR, markerAfter);
    }
  }
  // ** Add Markers

  // ** Remove Markers
  public RemoveMarkers() {
    this.markers.forEach((marker) => {
      marker.map = null;
    });
    this.markers = [];
    this.assets.next([]);
  }

  // ** Hide Assets
  public HideAssets() {
    this.markers.forEach((marker) => {
      marker.map = null;
    });
  }
  // ** Hide Assets

  // ** Show Assets
  public ShowAssets() {
    this.markers.forEach((marker) => {
      marker.map = this.map;
    });
  }
  // ** Show Assets

  //** Hide QR */
  public HideQrs() {
    this.qr_markers.forEach((marker) => {
      marker.map = null;
    });
  }
  //** Hide QR */

  //** Show QR */
  public ShowQrs() {
    this.qr_markers.forEach((marker) => {
      marker.map = this.map;
    });
  }
  //** Show QR */

  async calculateTotalSiteArea(
    siteCoordinates: SiteFencingModel[],
  ): Promise<string> {
    let totalArea = 0;
    // Ensure geometry library is loaded
    const { spherical } = await this.importGeometryLibrary();

    siteCoordinates.forEach((site) => {
      if (site.coordinates && site.coordinates.length > 2) {
        const path: google.maps.LatLng[] = site.coordinates.map(
          (coord) => new google.maps.LatLng(coord),
        );
        const area = spherical.computeArea(path);
        totalArea += area;
      }
    });

    return Math.round(totalArea).toLocaleString();
  }

  //** Handle site coverage */
  public HandleSiteCoverage(site: SiteModel): void {
    const siteFencings: SiteFencingModel[] =
      site.locations?.site?.coordinate?.site_coordinates?.map((fencing) => ({
        id: fencing.id,
        coordinates: fencing.coordinates || [],
      })) || [];

    const zoneFencings: ZoneFencingModel[] =
      site.locations?.site?.coordinate?.zone_coordinates?.map((zone) => ({
        id: zone.id,
        coordinates: zone.coordinates || [],
      })) || [];

    this.calculateSiteCoverage(siteFencings, zoneFencings);
  }

  //** Calculate site coverage */
  private async calculateSiteCoverage(
    siteFencings: SiteFencingModel[],
    zones: ZoneFencingModel[],
  ): Promise<void> {
    if (!siteFencings?.length || !zones?.length) {
      this.zoneCoveragePercentage = 0;
      this.coverageValidationFailed.next(true);
      return;
    }

    const totalSiteArea = await this.computeTotalArea(
      siteFencings.map((site) => site.coordinates),
    );
    const totalZoneArea = await this.computeTotalArea(
      zones.map((zone) => zone.coordinates),
    );

    if (totalSiteArea === 0) {
      this.zoneCoveragePercentage = 0;
      this.coverageValidationFailed.next(true);
      return;
    }

    const coveragePercentage = (totalZoneArea / totalSiteArea) * 100;
    this.zoneCoveragePercentage =
      coveragePercentage >= 90
        ? 100
        : Math.min(100, Math.max(0, coveragePercentage));
    this.coverageValidationFailed.next(this.zoneCoveragePercentage < 90);
  }

  //** Compute total area */
  private async computeTotalArea(
    coordinateGroups: ZoneCoordinate[][],
  ): Promise<number> {
    const { spherical } = await this.importGeometryLibrary();

    return coordinateGroups.reduce((total, coordinates) => {
      if (!coordinates?.length) return total;

      const polygon: google.maps.LatLngLiteral[] = coordinates.map((coord) => ({
        lat: coord.lat,
        lng: coord.lng,
      }));

      return total + spherical.computeArea(polygon);
    }, 0);
  }

  private async importGeometryLibrary() {
    // Import geometry library and return the spherical namespace
    const geometryLib = (await this.loader.importLibrary(
      "geometry",
    )) as google.maps.GeometryLibrary;
    return geometryLib;
  }

  // ** Remove Polygons
  public RemovePolygons() {
    this.sitePolygons.forEach((polygon) => {
      polygon.setMap(null);
    });
    this.zonePolygons.forEach((polygon) => {
      polygon.setMap(null);
    });
    this.sitePolygons = [];
    this.zonePolygons = [];
    this.siteCoordinates = [];
    this.zoneCoordinates = [];
  }
  // ** Remove Polygons

  // !! Map Helper Methods
  private async AddMapStyles(user?: UserModel) {
    const { StyledMapType } = await this.importMapsLibrary("maps");
    const styledMaps = new StyledMapType(MAPBACKGROUND, { name: "Base" });
    this.map.mapTypes.set("styled_map", styledMaps);
    if (user?.preferences?.map?.mapType) {
      this.map.setMapTypeId(user.preferences.map.mapType);
    } else {
      this.map.setMapTypeId("satellite");
    }
  }

  private async importMapsLibrary(type: Library) {
    return (await this.loader.importLibrary(type)) as google.maps.MapsLibrary;
  }

  private async importMarkersLibrary(type: Library) {
    return (await this.loader.importLibrary(type)) as google.maps.MarkerLibrary;
  }

  private animateMarker(mapMarker: HTMLElement) {
    mapMarker.animate(
      [
        { transform: "translateY(0) scale(1)" },
        { transform: "translateY(-8px) scale(1.05)" },
        { transform: "translateY(0) scale(1)" },
      ],
      {
        duration: 800,
        easing: "ease-in-out",
        iterations: Infinity,
      },
    );
  }

  private stopMarkerAnimation(mapMarker: HTMLElement) {
    mapMarker.getAnimations().forEach((anim) => anim.cancel());
  }

  public async MaterilAssetMarker(
    linkedAssets: MM_LinkedAssetModel[],
  ): Promise<void> {
    if (!this.map) return;
    const allAssets = this.assets.getValue();
    if (!allAssets?.length) return;

    // 1️⃣ Linked asset UID set
    const linkedAssetIds = new Set(linkedAssets.map((asset) => asset.uid));

    // 2️⃣ Loop through ALL markers
    for (const advMarker of this.markers) {
      const content = advMarker.content as HTMLElement;
      if (!content) continue;

      const mapMarker = content.querySelector(".mapMarker") as HTMLElement;
      if (!mapMarker) continue;

      const assetId = mapMarker.getAttribute("id");
      if (!assetId) continue;

      const isLinked = linkedAssetIds.has(assetId);

      // 3️⃣ APPLY VISUAL STATE
      if (!isLinked) {
        mapMarker.style.opacity = "0.35";
        mapMarker.style.filter = "grayscale(100%)";
        mapMarker.style.pointerEvents = "none";
        mapMarker.style.transform = "scale(0.95)";
        this.stopMarkerAnimation(mapMarker); // ⛔ stop
      } else {
        mapMarker.style.opacity = "1";
        mapMarker.style.filter = "none";
        mapMarker.style.pointerEvents = "auto";
        mapMarker.style.transform = "scale(1)";
        this.animateMarker(mapMarker); // ✅ animate
      }

      // 4️⃣ 🔥 FORCE GOOGLE MAPS REPAINT
      advMarker.map = null;
      advMarker.map = this.map;
    }

    // 5️⃣ Optional visual feedback
    const zoom = this.map.getZoom() ?? 13;
    this.map.setZoom(zoom + 1);
  }

  // ** Fencing visibility (site + zone polygons, hidden for a clean map)
  public SetFencingVisible(isVisible: boolean): void {
    this.isFencingVisible = isVisible;
    [...this.sitePolygons, ...this.zonePolygons].forEach((polygon) =>
      polygon.setVisible(isVisible),
    );
  }
  // ** Fencing visibility
}
